import { EImportSectionsTarget } from '@constants';
import { ISection } from '@sdk/contracts';
import { Button, Popover, Flex, Tooltip, message } from 'antd';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTheme } from 'styled-components';

import { PopoverWrapper } from './SectionActions.styles';

import { Icon } from '@/components/Icon';
import { RemoveSection } from '@/components/RemoveSection/';
import { EditSection } from '@/components/SectionManagement/EditSection';
import { UnsyncSectionConfirmation, SyncSummary } from '@/components/Sections';
import { useMutateSection, useProfile } from '@/store';
import { ECourseContentStep, ECourseStep } from '@/types';

type Props = {
  courseId: string;
  section: ISection;
  target?: EImportSectionsTarget;
};

export const SectionActions: React.FC<Props> = ({
  courseId,
  section,
  target = EImportSectionsTarget.SECTIONS
}) => {
  const { t } = useTranslation('CourseTranslations');
  const { isWorking, updateSection, unsyncSection } = useMutateSection();
  const { search } = useLocation();
  const activeContentTab = new URLSearchParams(search).get('contentTab');
  const { colors } = useTheme();
  const isSectionsTab = activeContentTab === ECourseContentStep.SECTIONS;
  const {
    roleCheck: { isManager, isTeacher }
  } = useProfile();
  const displayEditSectionButton = isManager || isTeacher;

  const [isUnsyncSectionModalOpen, setIsUnsyncSectionModalOpen] = useState(false);
  const navigateTo = useNavigate();

  const handleGoToParentSection = () => {
    const isIntroductionTab = activeContentTab === ECourseContentStep.INTRODUCTION;

    if (isIntroductionTab) {
      navigateTo(
        `/content/base-content/${section.parentCourse?._id}?activeTab=${ECourseStep.CONTENT}&contentTab=${ECourseContentStep.INTRODUCTION}&sectionId=${section.parentSection?._id}`
      );
    } else {
      navigateTo(
        `/content/base-content/${section.parentCourse?._id}?activeTab=${ECourseStep.CONTENT}&contentTab=${ECourseContentStep.SECTIONS}&sectionId=${section.parentSection?._id}`
      );
    }
  };

  return (
    <>
      <UnsyncSectionConfirmation
        section={section}
        isModalOpen={isUnsyncSectionModalOpen}
        setIsModalOpen={setIsUnsyncSectionModalOpen}
        onUnsyncSection={() => unsyncSection(section._id)}
      />

      <Flex>
        <>
          {isManager && isSectionsTab && (
            <Tooltip
              title={t(section.active ? 'VISIBLE_SECTION_TOOLTIP' : 'HIDDEN_SECTION_TOOLTIP')}>
              <Button
                size="large"
                type="text"
                loading={isWorking}
                onClick={e => {
                  e.stopPropagation();
                  updateSection({ ...section, active: !section.active });
                }}
                icon={<Icon i={section.active ? 'View' : 'ViewOff'} size={20} />}
                style={{ color: colors.black9 }}
              />
            </Tooltip>
          )}

          {displayEditSectionButton && <EditSection target={target} section={section} />}

          {isManager && (
            <Tooltip title={t('COPY_SECTION_ID')}>
              <Button
                type="text"
                size="large"
                icon={<Icon i="Copy" size={20} />}
                onClick={e => {
                  e.stopPropagation();
                  navigator.clipboard.writeText(section._id);
                  message.success(t('SECTION_ID_COPIED', { name: section.name }));
                }}
              />
            </Tooltip>
          )}
        </>

        {isManager && (
          <Popover
            placement="right"
            trigger="click"
            content={
              <PopoverWrapper>
                {section.parentSection && (
                  <>
                    <Button
                      type="text"
                      loading={isWorking}
                      onClick={e => {
                        e.stopPropagation();
                        handleGoToParentSection();
                      }}
                      icon={<Icon i="Renew" size={16} />}>
                      {t('GO_TO_PARENT_SECTION')}
                    </Button>

                    <SyncSummary section={section} />

                    <Button
                      type="text"
                      loading={isWorking}
                      onClick={e => {
                        e.stopPropagation();
                        setIsUnsyncSectionModalOpen(true);
                      }}
                      icon={<Icon i="IbmDataReplication" size={16} />}>
                      {t('UNSYNC_SECTION')}
                    </Button>
                  </>
                )}

                <RemoveSection courseId={courseId} section={section} />
              </PopoverWrapper>
            }>
            <span onClick={e => e.stopPropagation()}>
              <Button
                size="large"
                type="text"
                icon={<Icon i="OverflowMenuVertical" size={20} />}
                style={{ color: colors.black9 }}
              />
            </span>
          </Popover>
        )}
      </Flex>
    </>
  );
};
